import { COLOR, COLUMN, COMPLIANCE, FORMATTING, JARGON, POSITION_TYPE } from "../../../modules/constants"
import { formatFloat, formatIntDate_D_MMM_YYYY, formatDate_D_MMM_YYYY } from "../../../modules/utilities"
const { right, string } = FORMATTING

const ftse350Bits = 3 // "FTSE 100" = 1 and "FTSE 250" = 2

export const getGovObj = props => {
  // console.log('getGovObj props:', props)
  const { governance, annualReport } = props
  const ethnic = governance.ethnic ?? governance.ethnicMinority
  const pcnt = getGovPercentages(governance)
  return {
    [COLUMN.PCNT_FEMALE]: {
      id: COLUMN.PCNT_FEMALE,
      color: pcnt.female < COMPLIANCE.PCNT.FEMALE ? COLOR.WARN : COLOR.GOOD,
      sortValue: pcnt.female,
      value: formatFloat(pcnt.female, 1),
    },
    [COLUMN.PCNT_INDEP_NON_EXECS]: {
      id: COLUMN.PCNT_INDEP_NON_EXECS,
      color: pcnt.nonExecs < COMPLIANCE.PCNT.NON_EXECS ? COLOR.WARN : COLOR.GOOD,
      sortValue: pcnt.nonExecs,
      value: formatFloat(pcnt.nonExecs, 1),
    },
    [COLUMN.SENIOR_FEMALE]: {
      id: COLUMN.SENIOR_FEMALE,
      color: governance.seniorFemale === 0 ? COLOR.WARN : COLOR.GOOD,
      value: governance.seniorFemale,
    },
    [COLUMN.INDEP_NON_EXEC_9]: {
      id: COLUMN.INDEP_NON_EXEC_9,
      color: governance.ninePlus > 0 ? COLOR.WARN : COLOR.GOOD,
      value: governance.ninePlus, // Non-Execs > 9 years tenure
    },
    [COLUMN.EM_DIRs]: {
      id: COLUMN.EM_DIRs,
      ...emDirsValue({ ...props, ethnic }),
      align: right,
      tooltip: emTooltip(annualReport, ethnic),
      link: getEmDirsLink(annualReport.url, ethnic?.pageNo),
      newTab: true,
      type: string,
    },
    [COLUMN.PARKER]: {
      id: COLUMN.PARKER,
      ...getParkerObj(props),
    },
    [COLUMN.COMPLIANT]: {
      id: COLUMN.COMPLIANT,
      ...getComplianceObj(props),
    }
  }
}

const parkerUrl = "https://s3.eu-west-1.amazonaws.com/annual-reports.numerable.io/2026/The-Parker-Review-March-2026.pdf"

// Returns link to EM Directors section of Annual Report, or Parker Review report if indicated by negative pageNo
const getEmDirsLink = (annualReportUrl, pageNo) => {
  if (pageNo == null)
    return null
  else if (pageNo > 0)
    return `${annualReportUrl}#page=${pageNo}`
  else
    return `${parkerUrl}#page=${Math.abs(pageNo)}` // Negative pageNo indicates Parker Review report, which is only available as a PDF on S3
}

const emTooltip = (annualReport, ethnic) => {
  return ethnic?.explanation ? `Annual Report ${getAnnualReportDate(annualReport)}states: ${ethnic?.explanation}` : null
}

const getAnnualReportDate = annualReport => {
  // console.log('annualReport:', annualReport)
  return annualReport?.dates?.last?.pub
    ? `(${formatIntDate_D_MMM_YYYY(annualReport.dates.last.doc)}) `
    : annualReport?.dates?.accountsDate
      ? `(${formatDate_D_MMM_YYYY(annualReport.dates.accountsDate)}) `
      : ""
}

const getComplianceObj = props => {
  // console.log('props:', props)
  const { governance } = props

  // Ethnic Minority currently named 'ethnicMinority' for lseProps and 'ethnic' for metadata
  // TODO: Rename ethnicMinority > ethnic
  const ethnic = governance.ethnic ?? governance.ethnicMinority
  const pcnt = getGovPercentages(governance)

  const count = (pcnt.nonExecs >= COMPLIANCE.PCNT.NON_EXECS ? 1 : 0) +
    (pcnt.female >= COMPLIANCE.PCNT.FEMALE ? 1 : 0) +
    (governance.seniorFemale > 0 ? 1 : 0) +
    (governance.ninePlus == 0 ? 1 : 0) +
    (!!ethnic?.numDirs ? 1 : 0)

  return {
    color: count === COMPLIANCE.TARGETS ? COLOR.GOOD : COLOR.WARN,
    sortValue: count,
    value: `${count} / ${COMPLIANCE.TARGETS}`,
  }
}

const getParkerObj = props => {
  // console.log('getParkerObj props:', props)
  const { governance, indexBit } = props
  // Ethnic Minority currently named 'ethnicMinority' for lseProps and 'ethnic' for metadata
  const ethnic = governance.ethnic ?? governance.ethnicMinority
  const { parker } = ethnic

  return parker === true
    ? {
      color: COLOR.GOOD,
      sortValue: 0,
      value: "yes",
    }
    : parker === false
      ? {
        color: COLOR.WARN,
        sortValue: 1,
        value: "no",
      }
      : indexBit & ftse350Bits
        ? {
          sortValue: 2,
          value: "?",
        }
        : {
          sortValue: 3,
          value: JARGON.UNKNOWN // "n/a"
        }
}

const emDirsValue = props => {
  // console.log('emDirsValue props:', props)
  const { companyName, ethnic, index } = props
  var i = 0
  const type = {
    unknown: i++,
    known: i++,
    greaterThan: i++,
    greaterThanOrEqual: i++,
    question: i++,
  }

  switch (ethnic?.type) {
    case type.known:
      return {
        color: ethnic.numDirs > 0 ? COLOR.GOOD : COLOR.WARN,
        value: ethnic.numDirs
      }
    case type.greaterThan:
      console.log(`${index} ${companyName}: ">" detected:`)
      return {
        // color: COLOR.GOOD,
        sortValue: ethnic.numDirs + 1.2,
        value: `${ethnic.numDirs + 1}+`
      }
    case type.greaterThanOrEqual:
      return {
        // color: COLOR.GOOD,
        sortValue: ethnic.numDirs + 0.2,
        value: `at least ${ethnic.numDirs}`
      }
    case type.question:
      if (ethnic.numDirs !== null)
        console.log(`${index} ${companyName}: "?" detected:`)
      return {
        color: ethnic.numDirs > 0 ? COLOR.GOOD : COLOR.WARN,
        sortValue: ethnic.numDirs ? ethnic.numDirs - 0.2 : -1, // So "2?" sorted before 2
        value: `${ethnic.numDirs ?? ""}?`
      }
    case type.unknown:
    default:
      return {
        sortValue: -2,
        value: JARGON.UNKNOWN
      }
  }
}

export const getTotDirectors = roleCounts =>
  roleCounts[POSITION_TYPE.CEO] +
  roleCounts[POSITION_TYPE.CFO] +
  roleCounts[POSITION_TYPE.OTHER_EXEC_DIRECTOR] +
  roleCounts[POSITION_TYPE.CHAIRMAN] +
  roleCounts[POSITION_TYPE.SENIOR_NON_EXEC] +
  roleCounts[POSITION_TYPE.NON_EXEC_DIRECTOR] +
  roleCounts[POSITION_TYPE.OTHER]

export const getGovPercentages = governance => {
  const { roleCounts } = governance
  const totDirectors = getTotDirectors(roleCounts)

  return {
    female: governance.female / (governance.female + governance.male) * 100,
    nonExecs: totDirectors - roleCounts[POSITION_TYPE.CHAIRMAN] > 0
      ? ((roleCounts[POSITION_TYPE.SENIOR_NON_EXEC] + governance.indNonExecDirs) / (totDirectors - roleCounts[POSITION_TYPE.CHAIRMAN])) * 100
      : null
  }
}

export default getGovObj
